import * as fuzzysort from '@slab/fuzzysort';
import _ from 'lodash';

type Indices = [start: number, end: number][];

export interface FuzzySearchMatch {
  score: number;
  key: string;
  value: string;
  refIndex: number;
  indices: Indices;
}

export interface FuzzySearchResult<T> {
  item: T;
  score: number;
  matches: FuzzySearchMatch[];
  refIndex: number;
}

const removeOverlaps: (indices: Indices) => Indices = (indices) => {
  const nonOverlappingIndices: Indices = [];

  indices
    .toSorted((a, b) => a[0] - b[0])
    .forEach((current) => {
      const last = _.last(nonOverlappingIndices);
      if (last && last[1] + 1 >= current[0]) {
        last[1] = Math.max(current[1], last[1]);
      } else {
        nonOverlappingIndices.push(current);
      }
    });

  return nonOverlappingIndices;
};

const getIndices = (result: fuzzysort.KeyResult<unknown>) => {
  const indices: Indices = [];
  let current: Indices[number] | null = null;

  // fuzzysort.indexes() returns an array of numbers representing the positions
  // that the match occurs at.
  // For example, if the search string is "abc" and the matched value is "abxc",
  // the indexes will be [0, 1, 3]. We want to convert it to [[0, 1], [3, 3]].
  fuzzysort.indexes(result).forEach((index) => {
    if (current?.[1] === index - 1) {
      current[1] = index;
      return;
    }

    if (current) {
      indices.push(current);
    }
    current = [index, index];
  });

  if (current) {
    indices.push(current);
  }

  return indices;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

interface FlattenedValue {
  value: string;
  objectIndex: number;
  valueIndex: number;
}

interface PreparedFlattenedValue extends Omit<FlattenedValue, 'value'> {
  value: fuzzysort.Prepared;
}

export class FuzzySearchIndex<T, K extends string> {
  readonly datasets: {
    key: K;
    values: PreparedFlattenedValue[];
  }[];

  constructor(private objects: T[], keys: K[]) {
    this.datasets = keys.map((key) => ({
      key,
      values: this.extractFlattenedValues(objects, key).map(
        ({ value, ...rest }) => ({
          value: fuzzysort.prepare(value),
          ...rest,
        }),
      ),
    }));
  }

  getObject({ objectIndex }: PreparedFlattenedValue): T | undefined {
    return this.objects[objectIndex];
  }

  private extractValuesAtPath(
    item: unknown,
    paths: string[],
    pathIndex: number,
  ): string[] {
    if (paths.length === pathIndex) {
      return typeof item === 'string' ? [item] : [];
    }

    const key = paths[pathIndex];
    if (!key || !isRecord(item)) return [];

    const value = item[key];
    return Array.isArray(value)
      ? value.flatMap((entry: unknown) =>
          this.extractValuesAtPath(entry, paths, pathIndex + 1),
        )
      : this.extractValuesAtPath(value, paths, pathIndex + 1);
  }

  /**
   * @example
   * extractFlattenedValues([{ tags: ['a'] }, { tags: [] }, { tags: ['b', 'c'] }], 'tags')
   * // [
   * //   { value: 'a', objectIndex: 0, valueIndex: 0 },
   * //   { value: 'b', objectIndex: 2, valueIndex: 0 },
   * //   { value: 'c', objectIndex: 2, valueIndex: 1 },
   * // ]
   */
  private extractFlattenedValues(
    objects: unknown[],
    key: string,
  ): FlattenedValue[] {
    const paths = key.split('.');

    return objects.flatMap((object, objectIndex) =>
      this.extractValuesAtPath(object, paths, 0).map((value, valueIndex) => ({
        value,
        objectIndex,
        valueIndex,
      })),
    );
  }
}

export enum FuzzySearchThreshold {
  Normal = -10000,
  Loose = -100000,
}

interface FuzzySearchOptions<T, K extends string> {
  /**
   * Default weight is 1.
   * Setting it to 0 will still match the key but not include it in the score.
   */
  weights?: Record<K, number>;
  /**
   * Filter out objects before searching.
   */
  filter?: (object: T) => boolean;
  threshold?: FuzzySearchThreshold;
}

const fuzzySearchSingleToken = <T, K extends string>(
  token: string,
  index: FuzzySearchIndex<T, K>,
  options: FuzzySearchOptions<T, K> = {},
) => {
  const { filter, weights, threshold = FuzzySearchThreshold.Normal } = options;

  const resultsMap = new Map<T, FuzzySearchResult<T>>();
  index.datasets.forEach(({ key, values }) => {
    const weight = weights?.[key] ?? 1;
    const results = fuzzysort.go(
      token,
      filter
        ? values.filter((value) => {
            const object = index.getObject(value);
            return object ? filter(object) : false;
          })
        : values,
      { threshold, key: 'value' },
    );

    // Fuzzysort returns a negative score from 0 to threshold.
    // We normalize it to a scale of 0 to 1.
    const normalizeFuzzysortScore = (fuzzysortScore: number) =>
      (fuzzysortScore - threshold) / (0 - threshold);

    results.forEach((result) => {
      const object = index.getObject(result.obj);
      if (!object) return;
      const score = normalizeFuzzysortScore(result.score) * weight;
      const match: FuzzySearchMatch = {
        key,
        score,
        value: result.target,
        indices: getIndices(result),
        refIndex: result.obj.valueIndex,
      };
      const resultFromOtherKeys = resultsMap.get(object);
      if (resultFromOtherKeys) {
        resultFromOtherKeys.score = Math.max(resultFromOtherKeys.score, score);
        resultFromOtherKeys.matches.push(match);
      } else {
        resultsMap.set(object, {
          item: object,
          score,
          matches: [match],
          refIndex: result.obj.objectIndex,
        });
      }
    });
  });
  return resultsMap;
};

const composeFilters = <T>(
  filter: (object: T) => boolean,
  existingFilter?: (object: T) => boolean,
) =>
  existingFilter
    ? (object: T) => filter(object) && existingFilter(object)
    : filter;

const fuzzySearch = <T, K extends string>(
  query: string,
  index: FuzzySearchIndex<T, K>,
  options: FuzzySearchOptions<T, K> = {},
) => {
  const trimmedQuery = query.trim();
  const [firstToken, ...restTokens] = trimmedQuery
    ? trimmedQuery.toLowerCase().split(/\s+/)
    : [];

  if (!firstToken) return [];

  const results = fuzzySearchSingleToken(firstToken, index, options);

  const optionsForRestTokens = {
    ...options,
    // Results should match all tokens so we filter out objects not included
    // in the results of previous tokens to improve performance.
    filter: composeFilters((object) => results.has(object), options.filter),
  };
  restTokens.forEach((token) => {
    const resultsForNextToken = fuzzySearchSingleToken(
      token,
      index,
      optionsForRestTokens,
    );
    results.forEach((result, object) => {
      const resultForNextToken = resultsForNextToken.get(object);
      if (!resultForNextToken) {
        results.delete(object);
        return;
      }

      // eslint-disable-next-line no-param-reassign
      result.score += resultForNextToken.score;

      resultForNextToken.matches.forEach((match) => {
        const existingMatch = result.matches.find(
          ({ key, refIndex }) =>
            key === match.key && refIndex === match.refIndex,
        );
        if (existingMatch) {
          existingMatch.score += match.score;
          existingMatch.indices = removeOverlaps([
            ...existingMatch.indices,
            ...match.indices,
          ]);
        } else {
          result.matches.push(match);
        }
      });
    });
  });

  return Array.from(results.values())
    .map((result) => ({
      ...result,
      score: result.score / (1 + restTokens.length),
    }))
    .toSorted((a, b) => b.score - a.score);
};

export default fuzzySearch;
